// src/pages/DriverMyTrips.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { CSVLink } from "react-csv";
import DriverLayout from "../components/DriverLayout";
import TripReplayMap from "../components/TripReplayMap";
import bookingWS from "../services/bookingWebSocket";
import { RefreshCw, ChevronLeft, ChevronRight, Search, Calendar, Clock, MapPin, Truck, User, AlertCircle, CheckCircle, XCircle } from "lucide-react";
import { fetchBookings } from "../actions/BookingActions";
import { fetchUsers } from "../actions/UserActions";
import { fetchVehicles } from "../actions/VehicleActions";

const DriverMyTrips = () => {
  const dispatch = useDispatch();
  const { authKey, userDetails } = useSelector((state) => state.auth);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchDriverTrips = useCallback(async () => {
    if (!authKey || !userDetails?.ServiceNo) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      const [allBookings, usersData, vehiclesData] = await Promise.all([
        dispatch(fetchBookings()),
        dispatch(fetchUsers()),
        dispatch(fetchVehicles())
      ]);

      const userMap = {};
      if (Array.isArray(usersData)) {
        usersData.forEach(user => {
          if (user.ServiceNo) userMap[user.ServiceNo] = user;
        });
      }

      const vehicleMap = {};
      if (Array.isArray(vehiclesData)) {
        vehiclesData.forEach(vehicle => {
          const key = vehicle.vehicleId || vehicle.vId;
          if (key) vehicleMap[key] = vehicle;
        });
      }

      if (Array.isArray(allBookings)) {
        const driverTrips = allBookings
          .filter((b) => b.dSNo === userDetails.ServiceNo)
          .map((booking) => {
            const employeeServiceNo = booking.bookingSno || booking.BookingSno;
            const employee = userMap[employeeServiceNo];
            const status = (booking.status || "").toString().trim().toUpperCase();
            
            const isPending = status === "P" || status === "PENDING";
            const isActiveTrip = status === "A" || status === "ACTIVE" || status === "EN" || status === "ENROUTE" || status === "IP" || status === "INPROGRESS";
            const isCompleted = status === "CO" || status === "COMPLETED";
            const isCancelled = status === "C" || status === "CANCEL" || status === "CANCELLED";

            const rawDistance = booking.totalDestence || booking.TotalDestence || booking.distance || null;
            const distance = rawDistance ? `${parseFloat(rawDistance).toFixed(1)} km` : "—";

            let duration = "—";
            if (booking.bsTime && booking.beTime) {
              const start = new Date(booking.bsTime);
              const end = new Date(booking.beTime);
              if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && start.getFullYear() > 1900 && end.getFullYear() > 1900 && end > start) {
                const mins = Math.round((end - start) / 60000);
                duration = mins > 0 ? `${mins} min` : "—";
              }
            }

            return {
              ...booking,
              bId: booking.bId || booking.BId,
              employeeSNo: employeeServiceNo || "N/A",
              employeeName: employee?.Name || booking.EmployeeName || booking.employeeName || "Unknown",
              vId: booking.vId || "N/A",
              vehicleType: vehicleMap[booking.vId || booking.vId]?.vehicleType || "Vehicle",
              pickupLocation: `Lat ${parseFloat(booking.slatitude).toFixed(4)}, Lng ${parseFloat(booking.slongitude).toFixed(4)}`,
              dropoffLocation: `Lat ${parseFloat(booking.elatitude).toFixed(4)}, Lng ${parseFloat(booking.elongitude).toFixed(4)}`,
              description: booking.Description || booking.description || "-",
              bookingDate: booking.bookingDT || booking.bookingDate,
              status,
              statusLabel: isPending ? "Pending" : isActiveTrip ? "Active" : isCompleted ? "Completed" : isCancelled ? "Cancelled" : "Unknown",
              statusColor: isPending ? "bg-amber-100 text-amber-700" : isActiveTrip ? "bg-emerald-100 text-emerald-700" : isCompleted ? "bg-blue-100 text-blue-700" : isCancelled ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-700",
              date: new Date(booking.bookingDT || booking.bookingDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
              time: new Date(booking.bookingDT || booking.bookingDate).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
              distance, duration,
              startTime: booking.bsTime && !booking.bsTime.includes("1/1/1900") ? new Date(booking.bsTime).toLocaleString("en-GB") : "—",
              endTime: booking.beTime && !booking.beTime.includes("1/1/1900") ? new Date(booking.beTime).toLocaleString("en-GB") : "—",
              slatitude: parseFloat(booking.slatitude), slongitude: parseFloat(booking.slongitude),
              elatitude: parseFloat(booking.elatitude), elongitude: parseFloat(booking.elongitude),
            };
          })
          .sort((a, b) => new Date(b.bookingDate) - new Date(a.bookingDate));

        setTrips(driverTrips);
      }
    } catch (err) {
      toast.error("Failed to load your trips");
      console.error("Trip fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [authKey, userDetails?.ServiceNo, dispatch]);

  useEffect(() => {
    fetchDriverTrips();
  }, [fetchDriverTrips]);

  useEffect(() => {
    if (!authKey || !userDetails?.ServiceNo) return;
    bookingWS.connect(authKey);
    const handleStatusUpdate = (message) => {
      if (message && message.bId) {
        setTrips(prevTrips => prevTrips.map(trip => {
          if (trip.bId?.toString() === message.bId?.toString()) {
            const newStatus = (message.status || "").toString().trim().toUpperCase();
            const isPending = newStatus === "P" || newStatus === "PENDING";
            const isActiveTrip = newStatus === "A" || newStatus === "ACTIVE" || newStatus === "EN" || newStatus === "ENROUTE" || newStatus === "IP" || newStatus === "INPROGRESS";
            const isCompleted = newStatus === "CO" || newStatus === "COMPLETED";
            const isCancelled = newStatus === "C" || newStatus === "CANCEL" || newStatus === "CANCELLED";
            return {
              ...trip,
              status: newStatus,
              statusLabel: isPending ? "Pending" : isActiveTrip ? "Active" : isCompleted ? "Completed" : isCancelled ? "Cancelled" : "Unknown",
              statusColor: isPending ? "bg-amber-100 text-amber-700" : isActiveTrip ? "bg-emerald-100 text-emerald-700" : isCompleted ? "bg-blue-100 text-blue-700" : isCancelled ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-700",
            };
          }
          return trip;
        }));
      }
    };
    bookingWS.onStatusUpdate(handleStatusUpdate);
    return () => bookingWS.disconnect();
  }, [authKey, userDetails?.ServiceNo]);

  const filteredTrips = useMemo(() => {
    return trips.filter((trip) => {
      const search = searchTerm.toLowerCase();
      const matchesSearch = trip.bId?.toString().includes(search) || trip.employeeName?.toLowerCase().includes(search) || trip.vId?.toLowerCase().includes(search);
      let matchesFilter = false;
      if (filterStatus === "ALL") matchesFilter = true;
      else if (filterStatus === "P") matchesFilter = trip.status === "P" || trip.status === "PENDING";
      else if (filterStatus === "ACTIVE") matchesFilter = trip.status === "A" || trip.status === "ACTIVE" || trip.status === "EN" || trip.status === "ENROUTE" || trip.status === "IP" || trip.status === "INPROGRESS";
      else if (filterStatus === "CO") matchesFilter = trip.status === "CO" || trip.status === "COMPLETED";
      else if (filterStatus === "C") matchesFilter = trip.status === "C" || trip.status === "CANCEL" || trip.status === "CANCELLED";
      return matchesSearch && matchesFilter;
    });
  }, [trips, searchTerm, filterStatus]);

  useEffect(() => setCurrentPage(1), [searchTerm, filterStatus]);

  const totalPages = Math.ceil(filteredTrips.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTrips = filteredTrips.slice(startIndex, startIndex + itemsPerPage);

  const stats = useMemo(() => {
    const pending = trips.filter(t => t.status === "P" || t.status === "PENDING").length;
    const active = trips.filter(t => t.status === "A" || t.status === "ACTIVE" || t.status === "EN" || t.status === "ENROUTE" || t.status === "IP" || t.status === "INPROGRESS").length;
    const completed = trips.filter(t => t.status === "CO" || t.status === "COMPLETED").length;
    const cancelled = trips.filter(t => t.status === "C" || t.status === "CANCEL" || t.status === "CANCELLED").length;
    const totalDistance = trips.reduce((sum, t) => sum + (parseFloat(t.distance) || 0), 0).toFixed(1);
    const totalMins = trips.reduce((sum, t) => sum + (parseInt(t.duration) || 0), 0);
    return { total: trips.length, pending, active, completed, cancelled, totalDistance, totalHours: totalMins >= 60 ? `${Math.floor(totalMins / 60)}h ${totalMins % 60}m` : totalMins > 0 ? `${totalMins}m` : "0m" };
  }, [trips]);

  const csvData = filteredTrips.map(t => ({ "Trip ID": t.bId, Vehicle: t.vId, Employee: t.employeeName, Pickup: t.pickupLocation, Dropoff: t.dropoffLocation, Date: t.date, Time: t.time, Duration: t.duration, Distance: t.distance, Status: t.statusLabel }));

  if (!authKey) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><p className="text-gray-500">Please login</p></div>;

  return (
    <DriverLayout>
      <div className="space-y-4">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3"><p className="text-xs text-gray-500">Total Trips</p><p className="text-xl font-semibold text-gray-900">{stats.total}</p></div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3"><p className="text-xs text-gray-500">Pending</p><p className="text-xl font-semibold text-gray-900">{stats.pending}</p></div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3"><p className="text-xs text-gray-500">Active</p><p className="text-xl font-semibold text-gray-900">{stats.active}</p></div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3"><p className="text-xs text-gray-500">Completed</p><p className="text-xl font-semibold text-gray-900">{stats.completed}</p></div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3"><p className="text-xs text-gray-500">Cancelled</p><p className="text-xl font-semibold text-gray-900">{stats.cancelled}</p></div>
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3"><p className="text-xs text-gray-500">Distance</p><p className="text-xl font-semibold text-gray-900">{stats.totalDistance} km</p></div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3"><p className="text-xs text-gray-500">Drive Time</p><p className="text-xl font-semibold text-gray-900">{stats.totalHours}</p></div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white border border-gray-100 rounded-lg shadow-sm p-4">
          <div className="relative mb-3">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search by ID, employee, or vehicle..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400" />
          </div>
          <div className="flex gap-2">
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 bg-white">
              <option value="ALL">All Trips</option><option value="P">Pending</option><option value="ACTIVE">Active</option><option value="CO">Completed</option><option value="C">Cancelled</option>
            </select>
            <CSVLink data={csvData} filename={`my-trips-${new Date().toISOString().split("T")[0]}.csv`} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${filteredTrips.length === 0 ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-gray-900 text-white hover:bg-gray-800"}`}>Export CSV</CSVLink>
          </div>
        </div>

        {/* Trips Table */}
        <div className="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex justify-between items-center">
            <h2 className="text-sm font-semibold text-gray-900">My Trips History</h2>
            <button onClick={fetchDriverTrips} disabled={loading} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /></button>
          </div>

          {loading ? (
            <div className="p-8 text-center"><div className="inline-block w-6 h-6 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin"></div><p className="text-xs text-gray-500 mt-2">Loading trips...</p></div>
          ) : filteredTrips.length === 0 ? (
            <div className="p-12 text-center"><p className="text-gray-400 text-sm">No trips found</p></div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr><th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500">Employee</th><th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500">Vehicle</th><th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500">Status</th><th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500">Date</th><th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500">ID</th><th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500">Route</th><th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500">Duration</th><th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500">Distance</th></tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {paginatedTrips.map((trip) => (
                      <tr key={trip.bId} onClick={() => { setSelectedTrip(trip); setIsModalOpen(true); }} className="hover:bg-gray-50/50 cursor-pointer transition">
                        <td className="py-2.5 px-3"><p className="text-xs font-medium text-gray-900">{trip.employeeName}</p><p className="text-[10px] text-gray-400">{trip.employeeSNo !== "N/A" && trip.employeeSNo}</p></td>
                        <td className="py-2.5 px-3"><span className="text-xs text-gray-700">{trip.vId}</span></td>
                        <td className="py-2.5 px-3"><span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${trip.statusColor}`}>{trip.statusLabel}</span></td>
                        <td className="py-2.5 px-3"><p className="text-xs text-gray-600">{trip.date}</p><p className="text-[10px] text-gray-400">{trip.time}</p></td>
                        <td className="py-2.5 px-3"><span className="text-xs font-mono font-medium text-gray-900">#{trip.bId}</span></td>
                        <td className="py-2.5 px-3"><p className="text-[10px] text-emerald-600 truncate max-w-[150px]">{trip.pickupLocation.substring(0, 30)}</p><p className="text-[10px] text-red-600 truncate max-w-[150px]">→ {trip.dropoffLocation.substring(0, 30)}</p></td>
                        <td className="py-2.5 px-3"><span className="text-xs text-gray-600">{trip.duration}</span></td>
                        <td className="py-2.5 px-3"><span className="text-xs text-gray-600">{trip.distance}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {filteredTrips.length > 0 && (
                <div className="border-t border-gray-100 px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-xs text-gray-500"><span>Show</span><select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="border border-gray-200 rounded px-2 py-1 text-xs"><option value={5}>5</option><option value={10}>10</option><option value={25}>25</option><option value={50}>50</option></select><span>per page</span></div>
                  <div className="text-xs text-gray-500">Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredTrips.length)} of {filteredTrips.length}</div>
                  <div className="flex gap-1"><button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50"><ChevronLeft size={14} className="rotate-180" /></button><button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50">Prev</button><span className="px-2 py-1 text-xs text-gray-600">{currentPage} / {totalPages}</span><button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50">Next</button><button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50"><ChevronRight size={14} /></button></div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Trip Details Modal */}
      {isModalOpen && selectedTrip && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setIsModalOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex justify-between items-center">
              <div><span className="text-xs text-gray-500">Trip ID</span><h2 className="text-base font-semibold text-gray-900">#{selectedTrip.bId}</h2></div>
              <button onClick={() => setIsModalOpen(false)} className="p-1 hover:bg-gray-100 rounded"><XCircle size={18} className="text-gray-500" /></button>
            </div>

            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="bg-gray-50 rounded-lg p-2"><p className="text-[10px] text-gray-500">Employee</p><p className="text-sm font-medium text-gray-900">{selectedTrip.employeeName}</p></div>
                <div className="bg-gray-50 rounded-lg p-2"><p className="text-[10px] text-gray-500">Vehicle</p><p className="text-sm font-medium text-gray-900">{selectedTrip.vId}</p></div>
                <div className="bg-gray-50 rounded-lg p-2"><p className="text-[10px] text-gray-500">Duration</p><p className="text-sm font-semibold text-gray-900">{selectedTrip.duration}</p></div>
                <div className="bg-gray-50 rounded-lg p-2"><p className="text-[10px] text-gray-500">Distance</p><p className="text-sm font-semibold text-gray-900">{selectedTrip.distance}</p></div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100"><p className="text-[10px] font-medium text-emerald-700 mb-1">PICKUP</p><p className="text-xs text-emerald-900 break-all">{selectedTrip.pickupLocation}</p></div>
                <div className="bg-red-50 rounded-lg p-3 border border-red-100"><p className="text-[10px] font-medium text-red-700 mb-1">DESTINATION</p><p className="text-xs text-red-900 break-all">{selectedTrip.dropoffLocation}</p></div>
              </div>

              <div className="flex flex-wrap gap-3 p-3 bg-gray-50 rounded-lg"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${selectedTrip.statusColor}`}>{selectedTrip.statusLabel}</span>{selectedTrip.startTime !== "—" && <div><p className="text-[10px] text-gray-500">Started</p><p className="text-xs text-gray-700">{selectedTrip.startTime}</p></div>}{selectedTrip.endTime !== "—" && <div><p className="text-[10px] text-gray-500">Ended</p><p className="text-xs text-gray-700">{selectedTrip.endTime}</p></div>}</div>

              {selectedTrip.description !== "-" && <div className="bg-amber-50 rounded-lg p-3 border-l-4 border-amber-400"><p className="text-xs text-amber-800">{selectedTrip.description}</p></div>}

              <div className="bg-gray-100 rounded-lg p-3"><h3 className="text-xs font-semibold text-gray-700 mb-2">Trip Route Map</h3><div className="rounded-lg overflow-hidden h-64"><TripReplayMap pickup={[selectedTrip.slatitude, selectedTrip.slongitude]} dropoff={[selectedTrip.elatitude, selectedTrip.elongitude]} pickupLabel={selectedTrip.pickupLocation} dropoffLabel={selectedTrip.dropoffLocation} vehicleType={selectedTrip.vehicleType} /></div></div>
            </div>
          </div>
        </div>
      )}
    </DriverLayout>
  );
};

export default DriverMyTrips;